import { locationUwbService } from '../../services/LocationUwbService';
export interface LocationRouteParams {
  siteId?: string | null;
  floorId?: string | null;
  tagId?: string | null;
  /** Encoded assignment windows from container/wafer history; null clears the param. */
  tagWindows?: string | null;
  /** Asset display name when scoped from container/wafer history; null clears the param. */
  scopeLabel?: string | null;
  scopeAssetType?: string | null;
  eventId?: string | number | null;
}

function setParam(params: URLSearchParams, key: string, value: string | number | null | undefined): void {
  if (value === undefined) {
    return;
  }

  if (value === null || value === '') {
    params.delete(key);
    return;
  }

  params.set(key, String(value));
}

export function getLocationQueryParams(): LocationRouteParams {
  const params = new URLSearchParams(window.location.search);
  return {
    siteId: params.get('siteId'),
    floorId: params.get('floorId'),
    tagId: params.get('tagId'),
    tagWindows: params.get('tagWindows'),
    scopeLabel: params.get('scopeLabel'),
    scopeAssetType: params.get('scopeAssetType'),
    eventId: params.get('eventId'),
  };
}

export function buildLocationPath(path: string, nextParams: LocationRouteParams = {}): string {
  const params = new URLSearchParams(window.location.search);
  setParam(params, 'siteId', nextParams.siteId);
  setParam(params, 'floorId', nextParams.floorId);
  setParam(params, 'tagId', nextParams.tagId);
  setParam(params, 'tagWindows', nextParams.tagWindows);
  setParam(params, 'scopeLabel', nextParams.scopeLabel);
  setParam(params, 'scopeAssetType', nextParams.scopeAssetType);
  setParam(params, 'eventId', nextParams.eventId);

  const nextQuery = params.toString();
  return nextQuery ? `${path}?${nextQuery}` : path;
}

export function navigateToLocationPath(path: string, nextParams: LocationRouteParams = {}): void {
  const nextPath = buildLocationPath(path, nextParams);
  window.history.pushState({}, '', nextPath);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function normalizeTagId(value: string): string {
  return value.trim().toUpperCase();
}

export function tagIdsMatch(left: string, right: string): boolean {
  return normalizeTagId(left) === normalizeTagId(right);
}

/** Resolve a tag's current site and floor from the location registry. */
export async function resolveLocationTagPlacement(
  tagId: string,
): Promise<{ siteId: string; floorId: string | null } | null> {
  const trimmed = tagId.trim();
  if (!trimmed) {
    return null;
  }

  const res = await locationUwbService.getAllLocationTags({ search: trimmed, limit: 50 });
  const match = (res.data?.tags ?? []).find((tag) => tagIdsMatch(tag.tagId, trimmed));
  if (!match) {
    return null;
  }

  return {
    siteId: match.siteId,
    floorId: match.floorId ?? null,
  };
}

/** Open Node Command Center focused on a tag (resolves site + floor when possible). */
export async function openNodeCommandCenterForTag(
  tagId: string,
  options: { tab?: 'live' | 'history' } = {},
): Promise<void> {
  const trimmed = tagId.trim();
  const placement = await resolveLocationTagPlacement(trimmed);
  const path = options.tab === 'history' ? '/location/history' : '/location';
  const nextPath = buildLocationPath(path, {
    tagId: trimmed,
    siteId: placement?.siteId ?? null,
    floorId: placement?.floorId ?? null,
    tagWindows: null,
    scopeLabel: null,
    scopeAssetType: null,
  });
  window.open(nextPath, '_blank', 'noopener,noreferrer');
}